import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { Upload, Download, Info, Moon, Sun, X, FileText, Loader2, RotateCw, RotateCcw, RefreshCw } from "lucide-react";
import { PDFDocument, degrees } from "pdf-lib";
import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf.mjs";
import ToolsNav from "../components/ToolsNav";
import { AdContainer, ResponsiveAd, StickyMobileAd } from "../components/AdPlacement";

pdfjsLib.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjsLib.version}/legacy/build/pdf.worker.min.mjs`;

function RotatePdf() {
  const [file, setFile] = useState(null);
  const [pdfBytes, setPdfBytes] = useState(null);
  const [pages, setPages] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [darkMode, setDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('darkMode');
      if (saved !== null) return JSON.parse(saved);
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return false;
  });
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
  }, [darkMode]);

  useEffect(() => {
    document.title = "Rotate PDF - Rotate PDF Pages - KayTool";
  }, []);

  const toggleDarkMode = () => setDarkMode(!darkMode);

  const handleFileSelect = async (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile && selectedFile.type === 'application/pdf') {
      setFile(selectedFile);
      await loadPdf(selectedFile);
    } else {
      toast.error("Please select a valid PDF file");
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && droppedFile.type === 'application/pdf') {
      setFile(droppedFile);
      await loadPdf(droppedFile);
    } else {
      toast.error("Please drop a valid PDF file");
    }
  };

  const loadPdf = async (pdfFile) => {
    setIsProcessing(true);
    try {
      const arrayBuffer = await pdfFile.arrayBuffer();
      setPdfBytes(new Uint8Array(arrayBuffer));
      
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      const pageItems = [];
      
      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const viewport = page.getViewport({ scale: 0.5 });
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        
        await page.render({ canvasContext: context, viewport }).promise;
        pageItems.push({
          pageNum: i,
          rotation: 0,
          dataUrl: canvas.toDataURL(),
          originalWidth: viewport.width,
          originalHeight: viewport.height
        });
      }
      
      setPages(pageItems);
      toast.success(`PDF loaded: ${pdf.numPages} pages`);
    } catch (error) {
      console.error("Error loading PDF:", error);
      toast.error("Failed to load PDF");
    }
    setIsProcessing(false);
  };

  const rotatePage = (pageNum, direction) => {
    setPages(prev => prev.map(page => {
      if (page.pageNum === pageNum) {
        const newRotation = direction === 'cw' 
          ? (page.rotation + 90) % 360 
          : (page.rotation - 90 + 360) % 360;
        return { ...page, rotation: newRotation };
      }
      return page;
    }));
  };

  const rotateAllPages = (direction) => {
    setPages(prev => prev.map(page => {
      const newRotation = direction === 'cw' 
        ? (page.rotation + 90) % 360 
        : (page.rotation - 90 + 360) % 360;
      return { ...page, rotation: newRotation };
    }));
    toast.success(`All pages rotated ${direction === 'cw' ? 'clockwise' : 'counter-clockwise'}`);
  };

  const resetRotations = () => {
    setPages(prev => prev.map(page => ({ ...page, rotation: 0 })));
    toast.success("All rotations reset");
  };

  const hasRotations = pages.some(p => p.rotation !== 0);

  const saveRotatedPdf = async () => {
    if (!pdfBytes || pages.length === 0) return;

    setIsProcessing(true);
    try {
      const pdfDoc = await PDFDocument.load(pdfBytes, { ignoreEncryption: true });
      const pdfPages = pdfDoc.getPages();
      
      pages.forEach((pageData, index) => {
        if (pageData.rotation !== 0) {
          const page = pdfPages[index];
          const currentRotation = page.getRotation().angle;
          page.setRotation(degrees(currentRotation + pageData.rotation));
        }
      });
      
      const rotatedPdfBytes = await pdfDoc.save();
      const blob = new Blob([rotatedPdfBytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.name.replace('.pdf', '_rotated.pdf');
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success("Rotated PDF downloaded!");
    } catch (error) {
      console.error("Error saving PDF:", error);
      toast.error("Failed to save PDF: " + error.message);
    }
    setIsProcessing(false);
  };

  const clearFile = () => {
    setFile(null);
    setPdfBytes(null);
    setPages([]);
  };

  const getRotationLabel = (rotation) => {
    if (rotation === 0) return null;
    return `${rotation}°`;
  };

  return (
    <div className={`min-h-screen transition-colors duration-300 ${darkMode ? 'dark bg-slate-900' : 'bg-gradient-to-br from-orange-50 to-amber-50'}`}>
      <Toaster position="top-center" richColors />
      
      <header className="border-b border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-amber-600 flex items-center justify-center">
              <RotateCw className="w-6 h-6 text-white" />
            </div>
            <span className="text-xl font-bold text-slate-800 dark:text-white">KayTool</span>
          </a>
          <div className="flex items-center gap-4">
            <ToolsNav />
            <Badge variant="outline" className="hidden sm:flex gap-1 dark:text-slate-300">
              <Info className="w-3 h-3" /> 100% Local
            </Badge>
            <Button variant="ghost" size="icon" onClick={toggleDarkMode} className="dark:text-slate-300">
              {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
          </div>
        </div>
      </header>

      <AdContainer className="mt-4 mb-0">
        <ResponsiveAd slot="above-fold-header" mobileSize="mobile-banner" desktopSize="banner" />
      </AdContainer>

      <main className="container mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-orange-500 to-amber-600 text-white rounded-full mb-4">
            <RotateCw className="w-5 h-5" />
            <span className="font-medium">Rotate PDF</span>
          </div>
          <h1 className="text-4xl font-bold text-slate-800 dark:text-white mb-4">
            Rotate PDF Pages
          </h1>
          <p className="text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
            Rotate individual pages or all pages by 90°, 180°, or 270°.
          </p>
        </div>

        {/* Upload Area */}
        {!file && (
          <Card 
            className={`border-2 border-dashed transition-all duration-300 dark:bg-slate-800 max-w-2xl mx-auto ${
              isDragging ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/20' : 'border-slate-300 dark:border-slate-600'
            }`}
            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={handleDrop}
          >
            <CardContent className="p-12 text-center">
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,application/pdf"
                onChange={handleFileSelect}
                className="hidden"
                data-testid="file-input"
              />
              <div 
                className="cursor-pointer"
                onClick={() => fileInputRef.current?.click()}
                data-testid="upload-zone"
              >
                <Upload className="w-16 h-16 mx-auto mb-4 text-orange-500" />
                <h3 className="text-xl font-semibold text-slate-700 dark:text-white mb-2">
                  Drop PDF or click to upload
                </h3>
                <p className="text-slate-500 dark:text-slate-400">
                  Select a PDF to rotate pages
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {file && (
          <div className="max-w-6xl mx-auto space-y-6">
            {/* Controls */}
            <Card className="dark:bg-slate-800">
              <CardContent className="p-4">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <FileText className="w-8 h-8 text-orange-500" />
                    <div>
                      <h3 className="font-semibold dark:text-white">{file.name}</h3>
                      <p className="text-sm text-slate-500 dark:text-slate-400">
                        {pages.length} pages {hasRotations && '• Modified'}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => rotateAllPages('ccw')}
                      title="Rotate all counter-clockwise"
                    >
                      <RotateCcw className="w-4 h-4 mr-1" /> All -90°
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => rotateAllPages('cw')}
                      title="Rotate all clockwise"
                    >
                      <RotateCw className="w-4 h-4 mr-1" /> All +90°
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={resetRotations}
                      disabled={!hasRotations}
                    >
                      <RefreshCw className="w-4 h-4 mr-1" /> Reset
                    </Button>
                    <Button variant="outline" size="sm" onClick={clearFile}>
                      <X className="w-4 h-4 mr-1" /> Clear
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Page Grid */}
            {isProcessing && pages.length === 0 ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                {pages.map((page) => (
                  <Card key={page.pageNum} className="overflow-hidden dark:bg-slate-800">
                    <div className="relative bg-slate-100 dark:bg-slate-700">
                      {/* Page Image with Rotation */}
                      <div 
                        className="flex items-center justify-center p-2"
                        style={{ minHeight: '150px' }}
                      >
                        <img
                          src={page.dataUrl}
                          alt={`Page ${page.pageNum}`}
                          className="max-w-full max-h-[180px] object-contain transition-transform duration-300"
                          style={{ transform: `rotate(${page.rotation}deg)` }}
                        />
                      </div>
                      
                      {/* Page Number Badge */}
                      <div className="absolute top-2 left-2">
                        <Badge className="bg-black/70">{page.pageNum}</Badge>
                      </div>
                      
                      {/* Rotation Badge */}
                      {page.rotation !== 0 && (
                        <div className="absolute top-2 right-2">
                          <Badge className="bg-orange-500">{page.rotation}°</Badge>
                        </div>
                      )}
                    </div>
                    
                    {/* Rotation Controls */}
                    <div className="p-2 flex justify-center gap-1 bg-white dark:bg-slate-800">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => rotatePage(page.pageNum, 'ccw')}
                        className="h-8 w-8 p-0"
                        title="Rotate counter-clockwise"
                      >
                        <RotateCcw className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => rotatePage(page.pageNum, 'cw')}
                        className="h-8 w-8 p-0"
                        title="Rotate clockwise"
                      >
                        <RotateCw className="w-4 h-4" />
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>
            )}

            {/* Save Button */}
            <div className="text-center">
              <Button 
                onClick={saveRotatedPdf}
                disabled={isProcessing}
                size="lg"
                className="bg-gradient-to-r from-orange-500 to-amber-600"
              >
                {isProcessing ? (
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                ) : (
                  <Download className="w-5 h-5 mr-2" />
                )}
                {hasRotations ? 'Save Rotated PDF' : 'Download PDF'}
              </Button>
            </div>

            {/* Quick Rotation Buttons */}
            <Card className="dark:bg-slate-800">
              <CardContent className="p-4">
                <h4 className="font-medium text-slate-700 dark:text-slate-200 mb-3">Quick Rotate All Pages</h4>
                <div className="flex flex-wrap gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      setPages(prev => prev.map(p => ({ ...p, rotation: 90 })));
                      toast.success("All pages set to 90°");
                    }}
                  >
                    90° CW
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      setPages(prev => prev.map(p => ({ ...p, rotation: 180 })));
                      toast.success("All pages set to 180°");
                    }}
                  >
                    180°
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      setPages(prev => prev.map(p => ({ ...p, rotation: 270 })));
                      toast.success("All pages set to 270°");
                    }}
                  >
                    270° (90° CCW)
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        <AdContainer className="mt-8">
          <ResponsiveAd slot="after-features" mobileSize="mobile-banner" desktopSize="banner" />
        </AdContainer>
      </main>

      <footer className="border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 py-8 mt-12">
        <div className="container mx-auto px-4 text-center text-sm text-slate-600 dark:text-slate-400">
          <p>&copy; {new Date().getFullYear()} KayTool. All processing happens locally in your browser.</p>
        </div>
      </footer>

      <StickyMobileAd slot="sticky-footer" />
    </div>
  );
}

export default RotatePdf;
